/**
 * Nhật ký hủy lịch phát của cụm loa — `/lich-phat/da-huy`.
 *
 * Dữ liệu thật từ `GET /api/v1/schedules?trangThai=da_huy`: lịch bị hủy là hủy
 * mềm (`trang_thai = 'da_huy'`, `updated_at = now()`), nên cột "Thời điểm hủy"
 * lấy `updatedAt` của dòng. Lọc theo địa bàn, tên lịch và khoảng ngày phát; lọc,
 * đếm, phân trang ở phía máy chủ, mặc định 20 dòng.
 *
 * Cùng phong cách với trang danh sách `/lich-phat` (`DanhSachLichPhat.tsx`) và
 * tái dùng ô lọc, ô hiển thị, hàm đổi ngày của trang đó.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { viLichNhatKy } from '../ngon-ngu/vi-lich-nhat-ky';
import { viDanhSachLichPhat } from '../ngon-ngu/vi-danh-sach-lich-phat';
import type { LichPhatNoiBoRa } from '../api/lich-phat-api';
import type { NutDiaBan, TrangDuLieu } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TruongLoc } from '../thanh-phan/MauGiaoDien';
import { ChonDiaBanCay } from '../thanh-phan/ChonDiaBanCay';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import {
  API_DANH_SACH_LICH_MAC_DINH,
  ngayVnSangEpoch,
  OKhoangNgay,
  ONgayGio,
  OSoNgayPhat,
  OThietBi,
  OTimTenLich,
  tenDiaBanCuaLich,
  type DanhSachLichPhatApi,
} from './DanhSachLichPhat';
import './danh-sach-lich-phat.css';

const t = viLichNhatKy.nhatKyHuy;
const tds = viDanhSachLichPhat;

export type NhatKyHuyLichPhatApi = Pick<DanhSachLichPhatApi, 'danhSach' | 'cayDiaBan'>;

export interface NhatKyHuyLichPhatProps {
  api?: NhatKyHuyLichPhatApi;
}

export function NhatKyHuyLichPhat({
  api = API_DANH_SACH_LICH_MAC_DINH,
}: NhatKyHuyLichPhatProps = {}): ReactNode {
  const [nguonId, datNguonId] = useState('');
  const [oTim, datOTim] = useState('');
  const [tuKhoa, datTuKhoa] = useState('');
  const [tuNgay, datTuNgay] = useState('');
  const [denNgay, datDenNgay] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState<number>(SO_DONG_MAC_DINH);

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayDiaBan({}, signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const dangLoc = nguonId !== '' || tuKhoa !== '' || tuNgay !== '' || denNgay !== '';

  const kq = dungTaiDuLieu<TrangDuLieu<LichPhatNoiBoRa>>({
    tai: (signal) =>
      api.danhSach(
        {
          page: trang,
          pageSize: soDong,
          trangThai: 'da_huy',
          nguonId: nguonId || undefined,
          keyword: tuKhoa || undefined,
          tuThoiDiem: ngayVnSangEpoch(tuNgay),
          denThoiDiem: ngayVnSangEpoch(denNgay, true),
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [trang, soDong, nguonId, tuKhoa, tuNgay, denNgay],
  });

  /** Đổi bộ lọc thì quay về trang 1. */
  const doiLoc =
    <V,>(dat: (v: V) => void) =>
    (v: V): void => {
      dat(v);
      datTrang(1);
    };

  const tim = (e?: FormEvent): void => {
    e?.preventDefault();
    datTuKhoa(oTim.trim());
    datTrang(1);
  };

  const xoaLoc = (): void => {
    datNguonId('');
    datOTim('');
    datTuKhoa('');
    datTuNgay('');
    datDenNgay('');
    datTrang(1);
  };

  const batDauStt = (trang - 1) * soDong;
  const dsDong = kq.duLieu?.items ?? [];

  const cot: CotBang<LichPhatNoiBoRa>[] = [
    { khoa: 'stt', nhan: t.cot.stt, canGiua: true, ve: (l) => batDauStt + dsDong.indexOf(l) + 1 },
    {
      khoa: 'thoiDiemHuy',
      nhan: t.cot.thoiDiemHuy,
      // Lịch bị hủy mềm: updated_at chính là lúc hủy.
      ve: (l) => <ONgayGio iso={l.updatedAt ?? l.createdAt} />,
    },
    { khoa: 'tenLich', nhan: t.cot.tenLich, ve: (l) => l.tenLichPhat },
    {
      khoa: 'maLich',
      nhan: t.cot.maLich,
      ve: (l) => <span className="dslp-khong-xuong">{l.lichPhatId}</span>,
    },
    { khoa: 'diaBan', nhan: t.cot.diaBan, ve: tenDiaBanCuaLich },
    { khoa: 'thietBi', nhan: t.cot.thietBi, canGiua: true, ve: (l) => <OThietBi l={l} /> },
    { khoa: 'ngayPhat', nhan: t.cot.ngayPhat, ve: (l) => <OSoNgayPhat l={l} /> },
    { khoa: 'nguoiTao', nhan: t.cot.nguoiTao, ve: (l) => l.tenNguoiTao ?? '—' },
  ];

  return (
    <section className="man-hinh dslp" data-man-hinh="nhat-ky-huy-lich-phat">
      <header className="man-hinh__dau">
        <h1>{t.tieuDe}</h1>
      </header>

      <div className="dslp-loc" role="search">
        <TruongLoc nhan={tds.diaBan} htmlFor="nkh-dia-ban">
          <ChonDiaBanCay
            id="nkh-dia-ban"
            className="dslp-cay"
            nhan={tds.diaBan}
            giaTri={nguonId}
            cay={cay.duLieu ?? []}
            nhanTatCa={tds.toanTinh}
            goiYTim={tds.timDiaBan}
            onDoi={doiLoc(datNguonId)}
          />
        </TruongLoc>
        <OTimTenLich id="nkh-ten" giaTri={oTim} onDoi={datOTim} onTim={tim} />
        <OKhoangNgay
          tienTo="nkh"
          tuNgay={tuNgay}
          denNgay={denNgay}
          onDoiTu={doiLoc(datTuNgay)}
          onDoiDen={doiLoc(datDenNgay)}
          goiY={t.goiYKhoangNgay}
        />
        <button
          type="button"
          className="nut nut--chinh dslp-nut-icon"
          data-hanh-dong="tai-lai-nhat-ky-huy"
          onClick={kq.taiLai}
        >
          <BieuTuong ten="lam-moi" co={16} />
          {tds.taiLai}
        </button>
      </div>

      <BangDuLieu<LichPhatNoiBoRa>
        nhan={t.tieuDe}
        cot={cot}
        dong={dsDong}
        khoaDong={(l) => l.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={t.rongMoTa}
      />
    </section>
  );
}
